import type {
  PercentGroup,
  QuestionProps,
} from "../../../../../../components/Questionnaire/Section/Question/types";
import type { VisibilityMap } from "../../../../../../components/Questionnaire/validation/form/types";
import type { Answers } from "../../../../types";
import type { FlattenGroupResult } from "../types";
import { createEmptyFlattenResult } from "../utils";
import { snakeCase } from "../../../../../../utils";

/**
 * percent_group
 * Flattens a percent_group into a single PercentageQuestion with hidden group questions.
 * Each group question becomes a label in the percentage split question, except for the
 * readonly total question which provides validation rules.
 */
export const flattenPercentGroup = (
  group: PercentGroup,
  answers: Answers,
  visibility: VisibilityMap = {},
): FlattenGroupResult => {
  const { title, groupQuestions = [] } = group;

  // Early return if no group questions exist
  if (!groupQuestions.length) {
    return createEmptyFlattenResult(answers, visibility);
  }

  // Separate regular questions from the readonly total question
  const regularQuestions = groupQuestions.filter(q => !q.readOnly);
  const totalQuestion = groupQuestions.find(q => q.readOnly === true);

  // Early return if no regular questions (only total questions)
  if (!regularQuestions.length) {
    return createEmptyFlattenResult(answers, visibility);
  }

  // Extract labels from regular questions using primaryText
  const regularQuestionLabels = regularQuestions.map(q => q.primaryText);
  const totalQuestionLabel = totalQuestion?.primaryText || "Total";

  const labels = [...regularQuestionLabels, totalQuestionLabel];

  // Build validation - always use percentages type with 0-100 range
  const validation = {
    type: "percentages" as const,
    value: { min: 0, max: 100 },
    message: totalQuestion?.validation?.message,
  };

  // Shadow question is visible if any of the group questions are visible
  const flattenedVisibility = groupQuestions.some(q => q.visible === true);

  /* Create the flattened percentage split question
    Uses PercentageGroupQuestion which maps labels to original question names
  */
  const flattenedQuestion: Partial<QuestionProps> = {
    name: `Shadow::${title}`,
    id: snakeCase(`shadow::${title}`),
    primaryText: title,
    display: {
      type: "percentage_split_group",
      config: {
        labels,
        step: 5,
      },
    },
    validation,
    errorMessage: totalQuestion?.errorMessage,
    required: false,
    visible: flattenedVisibility,
    hasSensitiveData: regularQuestions[0]?.hasSensitiveData ?? false,
    presentationGroup: totalQuestion?.presentationGroup,
    presentationText: totalQuestion?.presentationText,
    shouldUpdate: groupQuestions.some(q => q.shouldUpdate),
  };

  // Process group questions: hide them but preserve their answers
  const answersByQuestionName = { ...answers };
  const visibilityByQuestionName = { ...visibility };

  const hiddenGroupQuestions = groupQuestions.map(q => {
    /**
     * Answer handling for percent group questions:
     * - Current form answers state is preserved via spread above
     * - Question's own answer (from API/initial state) overrides if present
     * - Only stores q.answer if it exists; doesn't set defaults
     */
    if (q.answer) {
      answersByQuestionName[q.name] = q.answer;
    }
    visibilityByQuestionName[q.name] = false;

    /**
     * Set includeAnswer to true for regular questions so their answers are sent to the backend even when hidden.
     * Set includeAnswer to false for the total question (readOnly) as it should not be sent.
     */
    return { ...q, includeAnswer: !q.readOnly, visible: false };
  });

  // Return the flattened result
  return {
    questions: [flattenedQuestion, ...hiddenGroupQuestions],
    groups: {
      [flattenedQuestion.name!]: {
        kind: "percent",
        names: [
          ...regularQuestions.map(q => q.name),
          ...(totalQuestion ? [totalQuestion.name] : []),
        ],
      },
    },
    visibility: {
      ...visibilityByQuestionName,
      [flattenedQuestion.name!]: flattenedVisibility,
    },
    answers: answersByQuestionName,
  };
};
